import { useState } from "react";
import { FEEDBACK_CATEGORIES, type Feedback, parseFeedback } from "../../../supabase/functions/beta-feedback/policy";
import { i18n, useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";
import { submitBetaFeedback } from "./lib/betaFeedback";

const categoryLabels = {
  get bug() {
    return i18n.t("interfaceExtras:bug");
  },
  get idea() {
    return i18n.t("interfaceExtras:idea");
  },
  get usability() {
    return i18n.t("interfaceExtras:usability");
  },
  get other() {
    return i18n.t("interfaceExtras:other");
  },
};

export default function BetaFeedback() {
  const { t } = useTranslation("keyboard");
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Feedback["category"]>("idea");
  const [message, setMessage] = useState("");
  const [followUp, setFollowUp] = useState(false);
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState<Feedback>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [received, setReceived] = useState(false);
  const dialogRef = useDialogFocus<HTMLElement>(open, () => {
    if (!busy) setOpen(false);
  });

  async function submit() {
    if (!preview || busy) return;
    setError("");
    setBusy(true);
    try {
      await submitBetaFeedback(preview);
      setReceived(true);
      setMessage("");
      setEmail("");
      setFollowUp(false);
      setPreview(undefined);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : i18n.t("screenCopy:betaFeedback.submissionFailedYourFeedbackIsRetained"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="beta-feedback-trigger" onClick={() => setOpen(true)}>
        {i18n.t("screenCopy:betaFeedback.trialFeedback")}
      </button>
      {open && (
        <div className="record-export-backdrop beta-feedback-backdrop">
          <section
            ref={dialogRef}
            tabIndex={-1}
            className="paper-card record-export-dialog beta-feedback-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-feedback-title"
            aria-describedby="beta-feedback-privacy"
          >
            <header>
              <h2 id="beta-feedback-title">{i18n.t("screenCopy:betaFeedback.trialFeedback")}</h2>
              <button
                type="button"
                className="record-export-close"
                aria-label={i18n.t("screenCopy:betaFeedback.closeFeedback")}
                disabled={busy}
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </header>
            <p id="beta-feedback-privacy" className="beta-feedback-privacy">
              {i18n.t("screenCopy:betaFeedback.feedbackIsOptionalAndSentWithYour")}
            </p>
            {received ? (
              <div className="beta-feedback-success">
                <p role="status">{i18n.t("screenCopy:betaFeedback.feedbackReceivedThankYouForHelpingImprove")}</p>
                <button
                  type="button"
                  onClick={() => {
                    setReceived(false);
                    setCategory("idea");
                  }}
                >
                  {i18n.t("screenCopy:betaFeedback.writeMoreFeedback")}
                </button>
              </div>
            ) : preview ? (
              <>
                <h3>{i18n.t("screenCopy:betaFeedback.nothingIsSentUntilYouConfirm")}</h3>
                <dl className="beta-feedback-preview" aria-label={i18n.t("screenCopy:betaFeedback.feedbackToSubmit")}>
                  <dt>{i18n.t("gradeOverview:table.categoryFilter")}</dt>
                  <dd>{categoryLabels[preview.category]}</dd>
                  <dt>{i18n.t("screenCopy:betaFeedback.content")}</dt>
                  <dd>{preview.message}</dd>
                  <dt>{i18n.t("screenCopy:betaFeedback.followUpEmail")}</dt>
                  <dd>{preview.contactEmail || i18n.t("screenCopy:betaFeedback.noEmailProvided")}</dd>
                </dl>
                {busy && <p role="status">{t("feedbackSubmitting")}</p>}
                {error && (
                  <p className="form-error" role="alert">
                    {error}
                  </p>
                )}
                <footer>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setPreview(undefined);
                      setError("");
                    }}
                  >
                    {i18n.t("screenCopy:betaFeedback.backToEdit")}
                  </button>
                  <button type="button" className="primary-action" disabled={busy} onClick={() => void submit()}>
                    {busy
                      ? i18n.t("screenCopy:betaFeedback.submitting")
                      : i18n.t("screenCopy:betaFeedback.confirmAndSubmitFeedback")}
                  </button>
                </footer>
              </>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  setError("");
                  try {
                    setPreview(parseFeedback({ category, message, ...(followUp ? { contactEmail: email } : {}) }));
                  } catch (caught) {
                    setError(
                      caught instanceof Error
                        ? caught.message
                        : i18n.t("screenCopy:betaFeedback.checkTheFeedbackContent"),
                    );
                  }
                }}
              >
                <label>
                  {i18n.t("screenCopy:betaFeedback.feedbackCategory")}
                  <select
                    value={category}
                    onChange={(event) => setCategory(event.target.value as Feedback["category"])}
                  >
                    {FEEDBACK_CATEGORIES.map((value) => (
                      <option key={value} value={value}>
                        {categoryLabels[value]}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="beta-feedback-message">
                  <label htmlFor="beta-feedback-message">{i18n.t("screenCopy:betaFeedback.feedbackContent")}</label>
                  <textarea
                    id="beta-feedback-message"
                    required
                    maxLength={2000}
                    rows={5}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder={i18n.t("screenCopy:betaFeedback.whichFeatureHasAProblemOrWhat")}
                  />
                </div>
                <small>
                  {message.length} {i18n.t("screenCopy:betaFeedback.characters")}
                </small>
                <label className="beta-feedback-opt-in">
                  <input type="checkbox" checked={followUp} onChange={(event) => setFollowUp(event.target.checked)} />
                  {i18n.t("screenCopy:betaFeedback.iWouldLikeToProvideAnEmail")}
                </label>
                {followUp && (
                  <label>
                    {i18n.t("screenCopy:betaFeedback.followUpEmail")}
                    <input
                      type="email"
                      required
                      maxLength={254}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                    />
                  </label>
                )}
                {error && (
                  <p className="form-error" role="alert">
                    {error}
                  </p>
                )}
                <footer>
                  <button type="submit" className="primary-action">
                    {i18n.t("screenCopy:betaFeedback.previewFeedback")}
                  </button>
                </footer>
              </form>
            )}
          </section>
        </div>
      )}
    </>
  );
}
